import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  Pressable,
  ScrollView,
  Platform,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { router, useLocalSearchParams } from 'expo-router';
import { supabase } from '@/utils/supabase';
import { theme } from '@/constants/theme';
import {
  STATUS_CONFIG,
  LANGUAGE_LABELS,
  formatRelativeDate,
  buildShareUrl,
  copyToClipboard,
  ensureKursiPlayOpenAccessSession,
  type CourseStatus,
} from '@/utils/kursiPlay';
import { publishAiCourse } from '@/utils/kursiPlayCreatorService';

type CreatorPath = {
  id: string;
  slug: string;
  world_name: string;
  description: string | null;
  status: CourseStatus;
  language: string;
  primary_color: string;
  created_at: string;
  updated_at: string;
  published_at: string | null;
};

type PathStats = {
  lessonsCount: number;
  activeLearnersCount: number;
  completionsCount: number;
  avgXp: number;
};

async function loadCreatorPath(worldId: string): Promise<CreatorPath> {
  const { data, error } = await supabase
    .from('kp_worlds')
    .select('id, slug, world_name, description, status, language, primary_color, created_at, updated_at, published_at')
    .eq('id', worldId)
    .single();

  if (error || !data) throw new Error('המסלול לא נמצא');
  return data as CreatorPath;
}

async function loadPathStats(worldId: string): Promise<PathStats> {
  const [{ count: lessonsCount }, { data: worldProgressRows }, { count: completionsCount }] = await Promise.all([
    supabase.from('kp_lessons').select('id', { count: 'exact', head: true }).eq('world_id', worldId),
    supabase.from('kp_user_world_progress').select('total_xp_earned').eq('world_id', worldId),
    supabase.from('kp_user_world_progress').select('id', { count: 'exact', head: true }).eq('world_id', worldId).eq('is_complete', true),
  ]);

  const xpValues = (worldProgressRows ?? []).map((row: any) => Number(row.total_xp_earned ?? 0));
  const avgXp = xpValues.length > 0
    ? Math.round(xpValues.reduce((sum: number, value: number) => sum + value, 0) / xpValues.length)
    : 0;

  return {
    lessonsCount: lessonsCount ?? 0,
    activeLearnersCount: worldProgressRows?.length ?? 0,
    completionsCount: completionsCount ?? 0,
    avgXp,
  };
}

async function updatePathStatus(worldId: string, status: CourseStatus): Promise<void> {
  if (status === 'published') {
    await publishAiCourse(worldId);
    return;
  }

  const update: Record<string, unknown> = { status, updated_at: new Date().toISOString() };
  if (status === 'archived') update.archived_at = new Date().toISOString();

  const { error } = await supabase.from('kp_worlds').update(update).eq('id', worldId);
  if (error) throw new Error(error.message);
}

export default function CreatorPathManagementScreen() {
  const { worldId, courseId } = useLocalSearchParams<{ worldId?: string; courseId?: string }>();
  const canonicalWorldId = useMemo(() => worldId ?? courseId ?? '', [worldId, courseId]);

  const [path, setPath] = useState<CreatorPath | null>(null);
  const [stats, setStats] = useState<PathStats>({
    lessonsCount: 0,
    activeLearnersCount: 0,
    completionsCount: 0,
    avgXp: 0,
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    if (!canonicalWorldId) { setError('מזהה מסלול חסר'); setLoading(false); return; }
    setError(null);

    try {
      await ensureKursiPlayOpenAccessSession(supabase);
      const [worldData, statsData] = await Promise.all([
        loadCreatorPath(canonicalWorldId),
        loadPathStats(canonicalWorldId),
      ]);
      setPath(worldData);
      setStats(statsData);
    } catch (err: any) {
      setError(err?.message ?? 'שגיאה בטעינה');
    } finally {
      setLoading(false);
    }
  }, [canonicalWorldId]);

  useEffect(() => { void load(); }, [load]);

  const handleStatusChange = useCallback(async (newStatus: CourseStatus) => {
    if (!path || !canonicalWorldId) return;
    setActionLoading(newStatus);

    try {
      await ensureKursiPlayOpenAccessSession(supabase);
      await updatePathStatus(canonicalWorldId, newStatus);
      setPath((prev) => prev
        ? {
            ...prev,
            status: newStatus,
            published_at: newStatus === 'published' ? new Date().toISOString() : prev.published_at,
          }
        : prev);
    } catch (err: any) {
      setError(err?.message ?? 'שגיאה בעדכון הסטטוס');
    } finally {
      setActionLoading(null);
    }
  }, [path, canonicalWorldId]);

  const handleCopy = useCallback(async () => {
    if (!path) return;
    await copyToClipboard(buildShareUrl(path.slug));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [path]);

  const handleBack = useCallback(() => {
    if (router.canGoBack()) router.back();
    else router.replace('/learn/creator' as any);
  }, []);

  if (loading) {
    return (
      <View style={s.center}>
        <ActivityIndicator color={theme.colors.primary} size="large" />
        <Text style={s.loadingText}>טוען מסלול...</Text>
      </View>
    );
  }

  if (error || !path) {
    return (
      <View style={s.center}>
        <Text style={s.errEmoji}>😕</Text>
        <Text style={s.errText}>{error ?? 'המסלול לא נמצא'}</Text>
        <Pressable style={s.primaryBtn} onPress={handleBack}>
          <Text style={s.primaryBtnText}>חזרה</Text>
        </Pressable>
      </View>
    );
  }

  const sc = STATUS_CONFIG[path.status] ?? STATUS_CONFIG.draft;
  const isPublished = path.status === 'published';
  const isDraft = path.status === 'draft';
  const isHidden = path.status === 'archived';
  const primary = path.primary_color || theme.colors.primary;
  const lifecycleLabel = isDraft ? 'Draft' : isPublished ? 'Published' : 'Hidden';
  const lifecycleDescription = isDraft
    ? 'טיוטה פנימית בלבד. לומדים עדיין לא רואים את המסלול.'
    : isPublished
      ? 'גלוי ללומדים ופתוח להצטרפות.'
      : 'מוסתר מלומדים חדשים. אפשר להחזיר לפרסום בכל רגע.';

  return (
    <ScrollView style={s.bg} contentContainerStyle={s.container}>
      <View style={s.header}>
        <Pressable onPress={handleBack} style={s.backBtn} hitSlop={12} accessibilityLabel="חזרה">
          <Text style={s.backBtnText}>→</Text>
        </Pressable>
        <Text style={s.headerTitle} numberOfLines={2}>{path.world_name}</Text>
        <View style={[s.statusBadge, { backgroundColor: sc.bg }]}>
          <Text style={[s.statusText, { color: sc.color }]}>{sc.emoji} {sc.label}</Text>
        </View>
      </View>

      <View style={s.section}>
        <Text style={s.sectionTitle}>מצב פרסום נוכחי</Text>
        <Text style={s.actionTitle}>{lifecycleLabel}</Text>
        <Text style={s.actionSub}>{lifecycleDescription}</Text>
      </View>

      <View style={[s.banner, { backgroundColor: primary }]}>
        <Text style={s.bannerName} numberOfLines={2}>{path.world_name}</Text>
        {!!path.description && <Text style={s.bannerDesc} numberOfLines={3}>{path.description}</Text>}
        <View style={s.bannerMeta}>
          <Text style={s.bannerMetaText}>{LANGUAGE_LABELS[path.language as keyof typeof LANGUAGE_LABELS] ?? path.language}</Text>
        </View>
      </View>

      <View style={s.statsGrid}>
        <StatCard icon="👥" value={String(stats.activeLearnersCount)} label="לומדים פעילים" />
        <StatCard icon="📚" value={String(stats.lessonsCount)} label="שיעורים" />
        <StatCard icon="🏁" value={String(stats.completionsCount)} label="סיימו מסלול" />
        <StatCard icon="⚡" value={stats.avgXp > 0 ? String(stats.avgXp) : '—'} label="XP ממוצע" />
      </View>

      <View style={s.section}>
        <Text style={s.sectionTitle}>פעולות</Text>

        <Pressable
          style={[s.actionRow, s.actionRowHighlight]}
          onPress={() => router.push(`/learn/creator/edit/${canonicalWorldId}` as any)}
          accessibilityRole="button"
        >
          <View style={s.actionLeft}>
            <Text style={s.actionIcon}>✏️</Text>
            <View>
              <Text style={s.actionTitle}>עריכת מסלול</Text>
              <Text style={s.actionSub}>עדכון שיעורים, תרחישים ומידע</Text>
            </View>
          </View>
          <Text style={[s.actionChevron, { color: theme.colors.primary }]}>←</Text>
        </Pressable>

        {isPublished && (
          <Pressable
            style={s.actionRow}
            onPress={() => router.push(`/learn/path/${path.slug}` as any)}
            accessibilityRole="link"
          >
            <View style={s.actionLeft}>
              <Text style={s.actionIcon}>👁</Text>
              <View>
                <Text style={s.actionTitle}>תצוגת לומד</Text>
                <Text style={s.actionSub}>כך הלומדים רואים את המסלול</Text>
              </View>
            </View>
            <Text style={s.actionChevron}>←</Text>
          </Pressable>
        )}

        {isPublished && (
          <Pressable style={s.actionRow} onPress={handleCopy} accessibilityRole="button">
            <View style={s.actionLeft}>
              <Text style={s.actionIcon}>{copied ? '✅' : '🔗'}</Text>
              <View>
                <Text style={s.actionTitle}>{copied ? 'הקישור הועתק!' : 'העתקת קישור'}</Text>
                <Text style={s.actionSub}>{buildShareUrl(path.slug)}</Text>
              </View>
            </View>
          </Pressable>
        )}
      </View>

      <View style={s.section}>
        <Text style={s.sectionTitle}>ניהול מצב תוכן: Draft / Published / Hidden</Text>

        {isDraft && (
          <Pressable
            style={[s.statusBtn, { borderColor: '#10B981', backgroundColor: '#10B98112' }]}
            onPress={() => router.push(`/learn/creator/edit/${canonicalWorldId}` as any)}
            disabled={!!actionLoading}
          >
            <Text style={[s.statusBtnText, { color: '#10B981' }]}>Review לפני Publish</Text>
          </Pressable>
        )}

        {isPublished && (
          <Pressable
            style={[s.statusBtn, { borderColor: '#6B7280', backgroundColor: '#6B728012' }]}
            onPress={() => Alert.alert('Hide מהלומדים', 'להסתיר את המסלול מלומדים חדשים? ניתן לפרסם מחדש בכל רגע.', [
              { text: 'ביטול', style: 'cancel' },
              { text: 'הסתר', style: 'destructive', onPress: () => handleStatusChange('archived') },
            ])}
            disabled={!!actionLoading}
          >
            {actionLoading === 'archived'
              ? <ActivityIndicator color="#6B7280" size="small" />
              : <Text style={[s.statusBtnText, { color: '#6B7280' }]}>Hide (מוסתר מלומדים)</Text>}
          </Pressable>
        )}

        {isHidden && (
          <Pressable
            style={[s.statusBtn, { borderColor: '#10B981', backgroundColor: '#10B98112' }]}
            onPress={() => router.push(`/learn/creator/edit/${canonicalWorldId}` as any)}
            disabled={!!actionLoading}
          >
            <Text style={[s.statusBtnText, { color: '#10B981' }]}>Review ו-Publish מחדש</Text>
          </Pressable>
        )}
      </View>

      <View style={s.section}>
        <Text style={s.sectionTitle}>פרטי מערכת</Text>
        <MetaRow label="נוצר" value={formatRelativeDate(path.created_at)} />
        <MetaRow label="עודכן" value={formatRelativeDate(path.updated_at)} />
        {!!path.published_at && <MetaRow label="פורסם" value={formatRelativeDate(path.published_at)} />}
        <MetaRow label="מזהה מסלול" value={path.id} mono />
      </View>
    </ScrollView>
  );
}

function StatCard({ icon, value, label }: { icon: string; value: string; label: string }) {
  return (
    <View style={s.statCard}>
      <Text style={s.statIcon}>{icon}</Text>
      <Text style={s.statValue}>{value}</Text>
      <Text style={s.statLabel}>{label}</Text>
    </View>
  );
}

function MetaRow({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <View style={s.metaRow}>
      <Text style={[s.metaValue, mono && s.metaValueMono]}>{value}</Text>
      <Text style={s.metaLabel}>{label}</Text>
    </View>
  );
}

const BG = '#0B0B14';
const SURFACE = '#16142A';
const SURFACE2 = '#221F3D';
const TEXT = '#F0EFF8';
const TEXT_MED = '#9896B4';
const BORDER = '#2D2B50';

const s = StyleSheet.create({
  bg: { flex: 1, backgroundColor: BG },
  container: { paddingTop: 56, paddingBottom: 48, paddingHorizontal: 20, gap: 14, maxWidth: 780, alignSelf: 'center' as any, width: '100%' },
  center: { flex: 1, backgroundColor: BG, alignItems: 'center', justifyContent: 'center', gap: 10, paddingHorizontal: 20 },
  loadingText: { fontFamily: theme.fonts.regular, fontSize: 14, color: TEXT_MED },
  errEmoji: { fontSize: 26 },
  errText: { fontFamily: theme.fonts.medium, fontSize: 15, color: '#FCA5A5', textAlign: 'center' },

  primaryBtn: { backgroundColor: theme.colors.primary, borderRadius: 10, paddingHorizontal: 16, paddingVertical: 10 },
  primaryBtnText: { fontFamily: theme.fonts.bold, color: '#fff', fontSize: 14 },

  header: { flexDirection: 'row-reverse', alignItems: 'center', gap: 10 },
  backBtn: { width: 34, height: 34, borderRadius: 8, borderWidth: 1, borderColor: BORDER, backgroundColor: SURFACE2, alignItems: 'center', justifyContent: 'center' },
  backBtnText: { color: TEXT_MED, fontSize: 16 },
  headerTitle: { flex: 1, fontFamily: theme.fonts.bold, fontSize: 20, color: TEXT, textAlign: 'right' },
  statusBadge: { paddingHorizontal: 9, paddingVertical: 5, borderRadius: 999 },
  statusText: { fontFamily: theme.fonts.semiBold, fontSize: 12 },

  banner: { borderRadius: 16, padding: 16, gap: 7 },
  bannerName: { fontFamily: theme.fonts.bold, fontSize: 22, color: '#fff', textAlign: 'right' },
  bannerDesc: { fontFamily: theme.fonts.regular, fontSize: 14, color: 'rgba(255,255,255,0.88)', textAlign: 'right', lineHeight: 20 },
  bannerMeta: { flexDirection: 'row-reverse' },
  bannerMetaText: { fontFamily: theme.fonts.medium, fontSize: 12, color: 'rgba(255,255,255,0.92)' },

  statsGrid: { flexDirection: 'row-reverse', flexWrap: 'wrap', gap: 10 },
  statCard: { flexBasis: '48%', flexGrow: 1, backgroundColor: SURFACE, borderWidth: 1, borderColor: BORDER, borderRadius: 12, padding: 12, gap: 4 },
  statIcon: { fontSize: 16, textAlign: 'right' },
  statValue: { fontFamily: theme.fonts.bold, fontSize: 20, color: TEXT, textAlign: 'right' },
  statLabel: { fontFamily: theme.fonts.regular, fontSize: 12, color: TEXT_MED, textAlign: 'right' },

  section: { backgroundColor: SURFACE, borderWidth: 1, borderColor: BORDER, borderRadius: 14, padding: 14, gap: 10 },
  sectionTitle: { fontFamily: theme.fonts.bold, fontSize: 15, color: TEXT, textAlign: 'right' },

  actionRow: { borderWidth: 1, borderColor: BORDER, borderRadius: 12, backgroundColor: SURFACE2, padding: 12, gap: 8 },
  actionRowHighlight: { borderColor: `${theme.colors.primary}77` },
  actionLeft: { flexDirection: 'row-reverse', alignItems: 'center', gap: 10 },
  actionIcon: { fontSize: 16 },
  actionTitle: { fontFamily: theme.fonts.semiBold, color: TEXT, textAlign: 'right', fontSize: 14 },
  actionSub: { fontFamily: theme.fonts.regular, color: TEXT_MED, textAlign: 'right', fontSize: 12 },
  actionChevron: { alignSelf: 'flex-start', color: TEXT_MED, fontSize: 14 },

  statusBtn: { borderWidth: 1, borderRadius: 11, paddingVertical: 11, alignItems: 'center', justifyContent: 'center', minHeight: 44 },
  statusBtnText: { fontFamily: theme.fonts.bold, fontSize: 13 },

  metaRow: { flexDirection: 'row-reverse', justifyContent: 'space-between', alignItems: 'center', borderBottomWidth: 1, borderBottomColor: BORDER, paddingVertical: 8 },
  metaLabel: { fontFamily: theme.fonts.medium, fontSize: 12, color: TEXT_MED },
  metaValue: { fontFamily: theme.fonts.regular, fontSize: 12, color: TEXT, textAlign: 'right', flex: 1, marginStart: 14 },
  metaValueMono: { fontFamily: Platform.select({ ios: 'Menlo', android: 'monospace', web: 'monospace' }), fontSize: 11 },
});
